// "Export CSV with full details" (Salem 2026-09-29: CompanyIQ signals don't create leads — "allow
// the manager to export to CSV with full details" and hand the list out). Every CompanyIQ list
// keeps its own columns and gets the company's calling details appended from the directory:
// address, company number, phones, the decision maker and their email / phone / LinkedIn.
import api, { fetchBlobUrl } from "./api";
import { downloadCsv } from "./utils";

const DETAILS = [
  ["Company number", "companyNumber"], ["Address", "address"], ["Sector", "sector"],
  ["Employees", "employees"], ["Turnover", "turnover"], ["Website", "website"], ["Customer status", "customer"],
  ["Buying signals", "signalKinds"], ["Signal details", "signals"],
  ["Main phone", "mainPhone"], ["Other phones", "otherPhones"],
  ["Contact", "contact"], ["Contact title", "contactTitle"], ["Decision maker", "decisionMaker"],
  ["Contact email", "contactEmail"], ["Contact phone", "contactPhone"], ["Contact LinkedIn", "contactLinkedin"],
  ["More contacts", "moreContacts"],
];

/** Like downloadCsv, but each row carries `_id` (the directory company id) and the company's
 *  full details are fetched and appended. If the lookup fails, the list still downloads. */
// Progress for every export on every page (Salem 2026-10-08: "Export CSV — nothing happens while
// it builds"). The details lookup runs in batches and reports after each one; <ExportStatus/>
// in the app shell listens and draws the bar, so no page has to wire anything up.
const BATCH = 200;
export const EXPORT_EVENT = "repiq:export";
const emit = (detail) => { try { window.dispatchEvent(new CustomEvent(EXPORT_EVENT, { detail })); } catch { /* old browser */ } };

export async function downloadCsvWithDetailsCsv(columns, rows, filename) {
  const ids = Array.from(new Set(rows.map((r) => r._id).filter(Boolean)));
  const det = {};
  const total = ids.length;
  emit({ phase: "start", done: 0, total, rows: rows.length, filename });
  let failed = false;
  for (let i = 0; i < total; i += BATCH) {
    const chunk = ids.slice(i, i + BATCH);
    try { Object.assign(det, (await api.post("/api/directory/companies/details", { ids: chunk })).companies || {}); }
    catch { failed = true; }
    emit({ phase: "lookup", done: Math.min(total, i + chunk.length), total, rows: rows.length, filename });
  }
  const extra = DETAILS.map(([h]) => h).filter((h) => !columns.includes(h));
  const out = rows.map((r) => {
    const d = det[r._id] || {};
    const row = { ...r };
    DETAILS.forEach(([h, k]) => { if (extra.includes(h)) row[h] = d[k] ?? ""; });
    return row;
  });
  downloadCsv([...columns, ...extra], out, filename);
  emit({ phase: "done", done: total, total, rows: rows.length, filename, partial: failed });
}


// ---------------------------------------------------------------------------------------------
// Excel for every prospect list (Salem 2026-10-23: "Make sure that every export of prospects
// includes all relevant information and is nicely formatted as in the attached file"). The page
// hands over the list it is showing; the server writes the workbook — the page's own columns first,
// then the Companies / Signals / Contracts / Contacts / Calls sheets for the companies on it — and
// this downloads it. Any failure falls back to the old CSV, so an export never just fails.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const humanise = (s) => { const t = String(s || "list").replace(/-/g, " ").trim(); return t.charAt(0).toUpperCase() + t.slice(1); };

export async function downloadListXlsx({ columns, rows, label, sheetTitle, signal, recentlyExported }) {
  const total = new Set(rows.map((r) => r._id).filter(Boolean)).size;
  emit({ phase: "start", kind: "xlsx", done: 0, total, rows: rows.length, filename: label });
  try {
    let j = await api.post("/api/directory/export/list", {
      label, sheetTitle: sheetTitle || humanise(label), columns, rows, signal: signal || "",
      recentlyExported: !!recentlyExported });
    const until = Date.now() + 15 * 60 * 1000;
    while (j.status === "running") {
      if (Date.now() > until) throw new Error("The export is taking too long");
      emit({ phase: "lookup", kind: "xlsx", done: j.done || 0, total: j.total || total, rows: rows.length, filename: j.filename });
      await sleep(1200);
      j = await api.get(`/api/directory/export/progress/${j.jobId}`);
    }
    if (j.status !== "done") throw new Error(j.error || "The export did not finish");
    const url = await fetchBlobUrl(`/api/directory/export/download/${j.jobId}`);
    const a = document.createElement("a");
    a.href = url; a.download = j.filename || `repiq-${label}.xlsx`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    emit({ phase: "done", kind: "xlsx", done: total, total, rows: rows.length, filename: j.filename });
    return true;
  } catch (e) {
    emit({ phase: "done", kind: "xlsx", done: 0, total, rows: rows.length, filename: label, fallback: true });
    return false;
  }
}

/** The one call every prospect-list page already makes. It now produces the Excel workbook, and
 *  only drops back to the plain CSV (with the calling details appended) if that cannot be built.
 *  `opts.signal` names the signal kind(s) the list is about, so each company's row says WHY. */
export async function downloadCsvWithDetails(columns, rows, filename, opts = {}) {
  const label = String(filename || "list").replace(/\.csv$/i, "").replace(/-\d{4}-\d{2}-\d{2}$/, "");
  const ok = await downloadListXlsx({ columns, rows, label, sheetTitle: opts.sheetTitle,
    signal: opts.signal, recentlyExported: opts.recentlyExported });
  if (ok) return;
  return downloadCsvWithDetailsCsv(columns, rows, filename);
}
